import React, { useState, useEffect, useMemo } from 'react';
import { MattressProduct, ProductRecommendationResponse, ChatConfig } from '../types';
import ProductCard from './ProductCard';
import ErrorBoundary from './ErrorBoundary';
import { normalizeRecommendations, getProductTypes, extractPreferences } from '../utils/productRecommendationUtils';

// More flexible interfaces to handle various formats
interface ExpandedRecommendations extends ProductRecommendationResponse {
  // Standard format fields
  message_type: 'PRODUCT_RECOMMENDATIONS';
  products: MattressProduct[];
  
  // Additional fields from other formats
  recommendations?: {
    message_type: string;
    products: MattressProduct[];
    additionalMessage?: string;
  };
  text?: string;
  content?: {
    type: string;
    text?: string;
    recommendations?: {
      message_type: string;
      products: MattressProduct[];
      additionalMessage?: string;
    }
  };
}

export interface ProductGridProps {
  recommendations: ProductRecommendationResponse;
  onProductSelect: (product: MattressProduct) => void;
  config?: ChatConfig | null;
  isEmbed?: boolean;
}

// Add this component before the main ProductGrid component  
interface MatchingPreferenceProps {
  label: string;
  value: string | boolean;
  icon?: React.ReactNode;
}

const MatchingPreference: React.FC<MatchingPreferenceProps> = ({ label, value, icon }) => {
  if (!value && typeof value !== 'string') return null;
  
  return (
    <div className="inline-flex items-center px-2 py-1 m-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
      {icon && <span className="mr-1">{icon}</span>}
      <span>{label}</span>
    </div>
  );
};

/**
 * ProductGrid displays a grid of product recommendations with filtering options
 */
const ProductGrid: React.FC<ProductGridProps> = ({ 
  recommendations, 
  onProductSelect, 
  config = null,
  isEmbed = false
}) => {
  // State for managing product view and filtering
  const [activeProductType, setActiveProductType] = useState<string>('all');
  const [compactView, setCompactView] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [shareModalOpen, setShareModalOpen] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [shareSuccess, setShareSuccess] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<string>('');

  // Normalize recommendations for consistent format
  const normalizedRecommendations = useMemo((): ProductRecommendationResponse => {
    try {
      return normalizeRecommendations(recommendations);
    } catch (error) {
      console.error('Error normalizing recommendations:', error);
      return { message_type: 'PRODUCT_RECOMMENDATIONS' as const, products: [], additionalMessage: '', storeId: '' };
    }
  }, [recommendations]);
  
  // Extract products from normalized data
  const products = useMemo(() => normalizedRecommendations.products || [], [normalizedRecommendations]);
  
  // Check if we're rendering in an embed widget
  const isEmbedWidget = isEmbed || (window.location.pathname.includes('/embed/') && window.self !== window.top);
  
  // Detect if we're in a small container on mount and window resize
  useEffect(() => {
    const checkSize = () => {
      const gridContainer = document.querySelector('.product-grid-container');
      if (gridContainer) {
        const isSmall = gridContainer.clientWidth < 500;
        setCompactView(isSmall);
      }
    };
    
    // Initial check
    checkSize();
    
    // Add resize listener
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);
  
  // Extract product types for filtering
  const productTypes = useMemo(() => {
    return getProductTypes(normalizedRecommendations);
  }, [normalizedRecommendations]);
  
  // Use first type or empty string as default active type
  const [activeType, setActiveType] = useState<string>(productTypes[0] || '');
  
  // Add state for view mode
  const [activeView, setActiveView] = useState<'grid' | 'compare'>('grid');
  
  // Filter products by active type
  const filteredProducts = useMemo(() => {
    if (!activeType) return products;
    return products.filter(p => p.specifications?.type === activeType);
  }, [products, activeType]);

  // Extract user preferences from recommendations
  const preferences = useMemo(() => {
    try {
      return extractPreferences(normalizedRecommendations);
    } catch (error) {
      console.error('Error extracting preferences:', error);
      return {
        productTypes: [],
        sleepPositions: [],
        firmness: [],
        hasEdgeSupport: false,
        hasMotionIsolation: false,
        hasCoolingFeatures: false,
        hasThickerProfile: false,
        priceRange: null
      };
    }
  }, [normalizedRecommendations]);

  // Add helper to determine if a product matches key preferences
  const getProductMatchTags = (product: MattressProduct) => {
    const matches = [];
    
    // Check sleep position matches
    if (preferences.sleepPositions.length > 0 && product.sleepPositions) {
      const matchingSleepPositions = product.sleepPositions.filter(pos => 
        preferences.sleepPositions.includes(pos)
      );
      
      if (matchingSleepPositions.length > 0) {
        matches.push({
          label: `Great for ${matchingSleepPositions.join('/')} sleepers`,
          value: true
        });
      }
    }
    
    // Check firmness match
    if (preferences.firmness.length > 0 && product.specifications?.firmness) {
      if (preferences.firmness.includes(product.specifications.firmness)) {
        matches.push({
          label: `${product.specifications.firmness.charAt(0).toUpperCase() + product.specifications.firmness.slice(1)} firmness`,
          value: true
        });
      }
    }
    
    // Check special features
    if (preferences.hasCoolingFeatures && product.specifications?.coolingFeatures) {
      matches.push({
        label: "Cooling technology",
        value: true
      });
    }
    
    if (preferences.hasEdgeSupport && product.specifications?.edgeSupport) {
      matches.push({
        label: "Enhanced edge support",
        value: true
      });
    }
    
    if (preferences.hasMotionIsolation && product.specifications?.motionIsolation) {
      matches.push({
        label: "Motion isolation",
        value: true
      });
    }
    
    return matches;
  };

  // Validate phone number
  const validatePhoneNumber = (phone: string): boolean => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10;
  };

  // Format phone number as user types
  const formatPhoneNumber = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    let formatted = '';

    if (cleaned.length === 0) {
      return '';
    } else if (cleaned.length <= 3) {
      formatted = `(${cleaned}`;
    } else if (cleaned.length <= 6) {
      formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }

    return formatted;
  };

  // Handle phone number input
  const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setPhoneNumber(formatPhoneNumber(input));
    if (phoneError && validatePhoneNumber(input)) {
      setPhoneError('');
    }
  };

  // Function to handle sharing product recommendations via SMS
  const handleShareViaSMS = async () => {
    if (!phoneNumber || !validatePhoneNumber(phoneNumber)) {
      setPhoneError('Please enter a valid phone number');
      return;
    }

    setIsSending(true);
    setPhoneError('');

    try {
      // Extract products to share
      const productsToShare = products.map(product => ({
        id: product.id,
        name: product.name,
        price: product.price,
        imageUrl: product.imageUrl,
        productUrl: product.productUrl,
      }));

      // Create payload for share API
      const payload = {
        phoneNumber: phoneNumber.replace(/\D/g, ''),
        products: productsToShare,
        storeId: normalizedRecommendations.storeId || config?.storeInfo?.storeId || '',
        additionalMessage: normalizedRecommendations.additionalMessage || 'Here are some mattress recommendations for you.'
      };

      // Make API call to share recommendations
      const response = await fetch('/api/share-recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to send recommendations');
      }

      setShareSuccess(true);
      setTimeout(() => setShareModalOpen(false), 2000);
    } catch (error) {
      console.error('Error sharing recommendations:', error);
      setPhoneError('Failed to send. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // Empty state handling
  if (products.length === 0) {
    return (
      <div className="product-grid-empty text-center p-4 border border-gray-200 rounded-lg bg-white">
        <svg 
          className="w-12 h-12 mx-auto text-gray-400 mb-2" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={1.5} 
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" 
          />
        </svg>
        <p className="text-gray-700 font-medium">No recommendations available</p>
        <p className="text-gray-600 text-sm mt-1">
          {normalizedRecommendations.additionalMessage || "We couldn't find mattresses matching your criteria."}
        </p>
      </div>
    );
  }

  // Wrapped in Error Boundary to prevent crashes
  return (
    <ErrorBoundary>
      {/* If we're in an embed widget, use a more compact view */}
      {isEmbedWidget ? (
        <div className="product-recommendations-embed rounded-lg border border-gray-200 overflow-hidden bg-white">
          {/* Type tabs - Simplified for embed */}
          {productTypes.length > 1 && (
            <div className="type-tabs flex overflow-x-auto px-2 py-1 border-b border-gray-200">
              {productTypes.map(type => (
                <button
                  key={type}
                  onClick={() => setActiveType(type)}
                  className={`whitespace-nowrap px-2 py-1 mr-1 text-xs rounded-full border ${
                    activeType === type
                      ? 'bg-white font-medium border-2' 
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200'
                  }`}
                  style={activeType === type ? { 
                    borderColor: config?.branding?.colors?.primary || '#2563eb',
                    color: config?.branding?.colors?.primary || '#2563eb'
                  } : {}}
                >
                  {(type || 'other').replace('-', ' ')}
                </button>
              ))}
            </div>
          )}
          
          {/* Compact Product Grid for Embed */}
          <div className="product-grid p-2 grid gap-2 grid-cols-1 justify-items-center">
            {filteredProducts.map((product) => (
              <ProductCard 
                key={product.id} 
                product={product} 
                onClick={onProductSelect}
                config={config}
                matchTags={getProductMatchTags(product)}
              />
            ))}
          </div>
        </div>
      ) : (
        // Regular view for non-embed
        <div className="product-recommendations rounded-lg border border-gray-200 overflow-hidden bg-white">
          {/* Type tabs */}
          {productTypes.length > 1 && (
            <div className="type-tabs flex overflow-x-auto px-4 py-2 border-b border-gray-200">
              {productTypes.map(type => (
                <button
                  key={type}
                  onClick={() => setActiveType(type)}
                  className={`whitespace-nowrap px-4 py-1 mr-2 text-sm rounded-full border ${
                    activeType === type
                      ? 'bg-white font-medium border-2' 
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200'
                  }`}
                  style={activeType === type ? { 
                    borderColor: config?.branding?.colors?.primary || '#2563eb',
                    color: config?.branding?.colors?.primary || '#2563eb'
                  } : {}}
                >
                  {(type || 'other').replace('-', ' ')}
                </button>
              ))}
            </div>
          )}
          
          {/* Products in Grid View */}
          {activeView === 'grid' && (
            <div className="product-grid p-4 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 justify-items-center">
              {filteredProducts.map((product) => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  onClick={onProductSelect}
                  config={config}
                  matchTags={getProductMatchTags(product)}
                />
              ))}
            </div>
          )}
          
          {/* Products in Compare View */}
          {activeView === 'compare' && (
            <div className="product-compare overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-3 text-left text-sm font-medium text-gray-700 border-b border-gray-200 w-1/4">Features</th>
                    {filteredProducts.map(product => (
                      <th key={product.id} className="p-3 text-center border-b border-gray-200">
                        <div className="font-medium text-gray-900">{product.name}</div>
                        {product.price > 0 && (
                          <div className="text-sm text-gray-600">${product.price}</div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Firmness Row */}
                  <tr>
                    <td className="p-3 text-sm text-gray-700 font-medium border-b border-gray-200">Firmness</td>
                    {filteredProducts.map(product => (
                      <td key={`${product.id}-firmness`} className="p-3 text-center text-sm border-b border-gray-200">
                        {product.specifications?.firmness?.replace('-', ' ') || 'N/A'}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Type Row */}
                  <tr>
                    <td className="p-3 text-sm text-gray-700 font-medium border-b border-gray-200">Type</td>
                    {filteredProducts.map(product => (
                      <td key={`${product.id}-type`} className="p-3 text-center text-sm border-b border-gray-200">
                        {product.specifications?.type?.replace('-', ' ') || 'N/A'}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Height Row */}
                  <tr>
                    <td className="p-3 text-sm text-gray-700 font-medium border-b border-gray-200">Height</td>
                    {filteredProducts.map(product => (
                      <td key={`${product.id}-height`} className="p-3 text-center text-sm border-b border-gray-200">
                        {product.specifications?.height ? `${product.specifications.height}"` : 'N/A'}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Features Row */}
                  <tr>
                    <td className="p-3 text-sm text-gray-700 font-medium border-b border-gray-200">Key Features</td>
                    {filteredProducts.map(product => (
                      <td key={`${product.id}-features`} className="p-3 text-center text-sm border-b border-gray-200">
                        {product.features && product.features.length > 0 ? (
                          <ul className="text-left list-disc pl-5">
                            {product.features.slice(0, 3).map((feature, idx) => (
                              <li key={idx} className="mb-1">{feature}</li>
                            ))}
                          </ul>
                        ) : 'N/A'}
                      </td>
                    ))}
                  </tr>
                  
                  {/* Action Row */}
                  <tr>
                    <td className="p-3 text-sm text-gray-700 font-medium"></td>
                    {filteredProducts.map(product => (
                      <td key={`${product.id}-action`} className="p-3 text-center">
                        <button 
                          className="px-4 py-2 border rounded-md text-sm font-medium"
                          style={{ 
                            borderColor: config?.branding?.colors?.primary || '#2563eb',
                            color: config?.branding?.colors?.primary || '#2563eb'
                          }}
                          onClick={() => onProductSelect(product)}
                        >
                          View Details
                        </button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          
          {/* View Toggle and Share Buttons */}
          <div className="flex justify-between items-center px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="view-toggle flex rounded-md overflow-hidden border border-gray-300">
              <button
                className={`px-3 py-1 text-sm ${activeView === 'grid' ? 'bg-white font-medium' : 'bg-gray-50 text-gray-600'}`}
                onClick={() => setActiveView('grid')}
              >
                Grid
              </button>
              <button
                className={`px-3 py-1 text-sm border-l border-gray-300 ${activeView === 'compare' ? 'bg-white font-medium' : 'bg-gray-50 text-gray-600'}`}
                onClick={() => setActiveView('compare')}
              >
                Compare
              </button>
            </div>
            
            <button
              className="flex items-center text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-100"
              onClick={() => setShareModalOpen(true)}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share Results
            </button>
          </div>
          
          {/* Share Modal */}
          {shareModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-md w-full overflow-hidden shadow-xl">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Share Results</h3>
                  <button 
                    onClick={() => setShareModalOpen(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="p-6">
                  {shareSuccess ? (
                    <div className="text-center py-4">
                      <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-lg font-medium text-gray-800">Results Shared!</p>
                      <p className="text-gray-600">We've sent these mattress recommendations to the phone number you provided.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleShareViaSMS}>
                      <p className="mb-4 text-gray-600">Enter a phone number to share these mattress recommendations via SMS.</p>
                      
                      <div className="mb-4">
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                        <input
                          type="text"
                          id="phone"
                          value={phoneNumber}
                          onChange={handlePhoneInput}
                          placeholder="(555) 123-4567"
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          maxLength={14}
                          required
                        />
                        {phoneError && <p className="mt-1 text-sm text-red-600">{phoneError}</p>}
                      </div>
                      
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => setShareModalOpen(false)}
                          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSending}
                          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          style={{ 
                            backgroundColor: config?.branding?.colors?.primary || '#2563eb'
                          }}
                        >
                          {isSending ? 'Sending...' : 'Share'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </ErrorBoundary>
  );
};

export default ProductGrid; 